import { useCallback, useEffect, useRef, useState } from "react";
import { getFinancialYears, getLocations, getOrgChart } from "./orgChartApi";

/**
 * Parse the starting year out of a financial-year value (e.g. "2024-25" → 2024),
 * falling back to the row id so the latest year can always be picked.
 * @param {object} fy financial-year row
 * @returns {number}
 */
const startYear = (fy) =>
  parseInt(String(fy.financial_year_value || "").split(/[-/]/)[0], 10) || Number(fy.id) || 0;

/**
 * Owns everything the org-chart page fetches and the current filter selection.
 *
 * - Loads the financial-year and location filters once, defaulting to the latest
 *   year and the first location so the chart renders on first paint.
 * - Re-fetches whenever the selection changes: one request returns both the tree
 *   and the deactivated list, which the backend derives from a single scope pass.
 * - Chart responses are tagged with a fetch id; anything that resolves after a
 *   newer fetch started is dropped, so a slow response can't clobber a newer one.
 *
 * @returns {{
 *   financialYears: object[], locations: object[],
 *   selectedFYId: string, setSelectedFYId: (id: string) => void,
 *   selectedLocationId: string, setSelectedLocationId: (id: string) => void,
 *   treeData: object|null, deactivated: object[], loading: boolean,
 * }}
 */
export function useOrgChartData() {
  const [financialYears, setFinancialYears] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedFYId, setSelectedFYId] = useState("");
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [treeData, setTreeData] = useState(null);
  const [deactivated, setDeactivated] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchIdRef = useRef(0);

  const loadFilters = useCallback(async () => {
    const [fyRes, locRes] = await Promise.all([getFinancialYears(), getLocations()]);
    const fys = fyRes?.isSuccess ? fyRes.data?.data || [] : [];
    const locs = locRes?.isSuccess ? locRes.data?.data || locRes.data || [] : [];
    setFinancialYears(fys);
    setLocations(locs);
    if (fys.length) {
      const latest = fys.reduce((a, b) => (startYear(b) >= startYear(a) ? b : a));
      setSelectedFYId(String(latest.id));
    }
    if (locs.length) setSelectedLocationId(String(locs[0].id));
  }, []);

  useEffect(() => {
    loadFilters();
  }, [loadFilters]);

  useEffect(() => {
    if (!selectedFYId || !selectedLocationId) {
      setTreeData(null);
      setDeactivated([]);
      return;
    }
    const fetchId = ++fetchIdRef.current;
    const isCurrent = () => fetchId === fetchIdRef.current;
    const fetchChart = async () => {
      setLoading(true);
      const chartRes = await getOrgChart(selectedFYId, selectedLocationId);
      if (!isCurrent()) return;
      const view = chartRes?.isSuccess ? chartRes.data : null;
      setTreeData(view?.tree ?? null);
      setDeactivated(Array.isArray(view?.deactivated) ? view.deactivated : []);
      setLoading(false);
    };
    fetchChart();
  }, [selectedFYId, selectedLocationId]);

  return {
    financialYears,
    locations,
    selectedFYId,
    setSelectedFYId,
    selectedLocationId,
    setSelectedLocationId,
    treeData,
    deactivated,
    loading,
  };
}
